import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { PlayCircle, RefreshCw, Calendar, AlertTriangle } from 'lucide-react';
import Header from '../../components/ui/Header';
import { useAuth } from '../../contexts/AuthContext';
import PlaybackStatCards from './components/PlaybackStatCards';
import SongLevelTable from './components/SongLevelTable';
import TalentEngagementTable from './components/TalentEngagementTable';
import RewatchPatternChart from './components/RewatchPatternChart';
import TopBottomPerformers from './components/TopBottomPerformers';
import {
  getPlaybackSummaryStats,
  getSongLevelBreakdown,
  getTalentEngagement,
  getRewatchPatternsByLevel,
  getTopBottomPerformersBySong,
} from '../../services/videoPlaybackAnalyticsService';

const DATE_RANGES = [
  { label: '7d', value: 7 },
  { label: '30d', value: 30 },
  { label: '90d', value: 90 },
];

const VideoPlaybackAnalytics = () => {
  const { isSuperUser } = useAuth();
  const [dateRange, setDateRange] = useState(30);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [summaryStats, setSummaryStats] = useState(null);
  const [songLevelData, setSongLevelData] = useState([]);
  const [talentData, setTalentData] = useState([]);
  const [rewatchData, setRewatchData] = useState([]);
  const [performers, setPerformers] = useState(null);

  useEffect(() => {
    if (!isSuperUser()) return;
    fetchAll();
  }, [dateRange]);

  const fetchAll = async () => {
    setLoading(true);
    setError(null);
    try {
      const [summaryRes, songRes, talentRes, rewatchRes, performersRes] = await Promise.all([
        getPlaybackSummaryStats(dateRange),
        getSongLevelBreakdown(dateRange),
        getTalentEngagement(dateRange),
        getRewatchPatternsByLevel(dateRange),
        getTopBottomPerformersBySong(dateRange),
      ]);

      if (summaryRes?.error) throw summaryRes?.error;
      if (songRes?.error) throw songRes?.error;
      if (talentRes?.error) throw talentRes?.error;
      if (rewatchRes?.error) throw rewatchRes?.error;
      if (performersRes?.error) throw performersRes?.error;

      setSummaryStats(summaryRes?.data);
      setSongLevelData(songRes?.data || []);
      setTalentData(talentRes?.data || []);
      setRewatchData(rewatchRes?.data || []);
      setPerformers(performersRes?.data);
    } catch (err) {
      console.error('VideoPlaybackAnalytics fetchAll error:', err);
      setError(err?.message || 'Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  if (!isSuperUser()) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#6e32b9] to-[#32215d]">
        <Header />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <AlertTriangle className="w-12 h-12 text-amber-400 mx-auto mb-3" />
            <p className="text-white font-semibold text-lg">Access Restricted</p>
            <p className="text-white/50 text-sm mt-1">SuperUser privileges required to view this page.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Video Playback Analytics | RoleCall</title>
        <meta name="description" content="View counts, completion rates, and rewatch patterns by talent and song" />
      </Helmet>
      <div className="min-h-screen bg-gradient-to-br from-[#6e32b9] to-[#32215d]">
        <Header />

        <main className="container mx-auto px-4 sm:px-6 py-8 max-w-7xl">
          {/* Page Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-white/10 rounded-xl">
                <PlayCircle className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-white">Video Playback Analytics</h1>
                <p className="text-purple-200 text-sm mt-0.5">
                  View counts, completion rates &amp; rewatch patterns by talent and song
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {/* Date range selector */}
              <div className="flex items-center gap-1 bg-white/10 rounded-xl p-1">
                <Calendar className="w-4 h-4 text-purple-300 ml-2" />
                {DATE_RANGES?.map(({ label, value }) => (
                  <button
                    key={value}
                    onClick={() => setDateRange(value)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                      dateRange === value
                        ? 'bg-white text-purple-900 shadow'
                        : 'text-purple-200 hover:text-white hover:bg-white/10'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>

              <button
                onClick={fetchAll}
                disabled={loading}
                className="p-2.5 bg-white/10 hover:bg-white/20 text-white rounded-xl transition-all disabled:opacity-50"
                title="Refresh data"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>

          {/* Error state */}
          {error && (
            <div className="bg-red-500/20 border border-red-500/30 rounded-2xl p-5 mb-6 flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-red-200 font-medium">Error loading analytics</p>
                <p className="text-red-300/70 text-sm mt-0.5">{error}</p>
                <button
                  onClick={fetchAll}
                  className="mt-3 px-4 py-1.5 bg-red-500/30 hover:bg-red-500/50 text-white text-sm rounded-lg transition-colors"
                >
                  Retry
                </button>
              </div>
            </div>
          )}

          {/* 1. Summary Stat Cards */}
          <section className="mb-8">
            <PlaybackStatCards stats={summaryStats} loading={loading} />
          </section>

          {/* 2. Rewatch Pattern Chart */}
          <section className="mb-8">
            <RewatchPatternChart data={rewatchData} loading={loading} />
          </section>

          {/* 3. Song Level Breakdown Table */}
          <section className="mb-8">
            <SongLevelTable data={songLevelData} loading={loading} />
          </section>

          {/* 4. Talent Engagement Table */}
          <section className="mb-8">
            <TalentEngagementTable data={talentData} loading={loading} />
          </section>

          {/* 5. Top / Bottom Performers */}
          <section className="mb-8">
            <div className="mb-4">
              <h2 className="text-white font-bold text-lg">Song Performance Rankings</h2>
              <p className="text-purple-300 text-sm mt-0.5">
                Top and bottom songs by completion rate (min. 3 views)
              </p>
            </div>
            <TopBottomPerformers data={performers} loading={loading} />
          </section>
        </main>
      </div>
    </>
  );
};

export default VideoPlaybackAnalytics;
